import { CircleAlert, Info, TriangleAlert, X } from "lucide-react";
import { useActiveAnnouncements, useDismissAnnouncement } from "@/hooks/use-announcements";
import { bgVar, fgVar, type StatusKey } from "@/lib/status-colors";
import { cn } from "@/lib/utils";
import type { AnnouncementSeverity } from "@/schemas/announcements";

// Severity drives the icon (fixed) and which status-color CSS variables to
// read for background / foreground. Admins customize the three severity
// tints from the Appearance picker; the icon shape is not user-settable so
// a "critical" banner can never visually demote itself to look like "info".
const severityMeta: Record<AnnouncementSeverity, { icon: typeof Info; statusKey: StatusKey }> = {
  info: { icon: Info, statusKey: "announcement-info" },
  warning: { icon: TriangleAlert, statusKey: "announcement-warning" },
  critical: { icon: CircleAlert, statusKey: "announcement-critical" },
};

export function AnnouncementBanner() {
  const announcements = useActiveAnnouncements();
  const dismissMutation = useDismissAnnouncement();

  if (announcements.length === 0) return null;

  return (
    <div className="space-y-2 border-b bg-background px-4 py-2 md:px-6">
      {announcements.map((a) => {
        const { icon: Icon, statusKey } = severityMeta[a.severity];
        const bannerStyle = {
          backgroundColor: `var(${bgVar(statusKey)})`,
          color: `var(${fgVar(statusKey)})`,
          // Border color tracks the foreground at low opacity so the banner
          // edge always reads against the picked background.
          borderColor: `color-mix(in srgb, var(${fgVar(statusKey)}) 25%, transparent)`,
        };
        return (
          <div
            key={a.id}
            role={a.severity === "critical" ? "alert" : "status"}
            style={bannerStyle}
            className={cn("flex items-start gap-3 rounded-md border px-4 py-2 text-sm shadow-xs")}
          >
            <Icon className="mt-0.5 size-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{a.title}</div>
              {a.body && (
                <div className="mt-0.5 whitespace-pre-line text-sm opacity-90">{a.body}</div>
              )}
            </div>
            {a.dismissible && (
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => dismissMutation.mutate(a.id)}
                className="-mr-1 inline-flex size-7 shrink-0 items-center justify-center rounded-md opacity-80 hover:bg-foreground/10 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-current focus-visible:outline-none"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
